import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY_POST = {
  id: null,
  title: "",
  excerpt: "",
  cover_image: "",
  content: "",
  published: true,
};

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("leads");
  const [editing, setEditing] = useState(null); // post being edited/created

  const H = { headers: { "X-Admin-Token": token } };

  const loadAll = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [l, p, po, s] = await Promise.all([
        api.get("/leads", H),
        api.get("/payments", H),
        api.get("/admin/posts", H),
        api.get("/newsletter", H),
      ]);
      setLeads(l.data);
      setPayments(p.data);
      setPosts(po.data);
      setSubs(s.data);
      setAuthed(true);
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setLoading(false);
    }
  };

  const refreshPosts = async () => {
    const po = await api.get("/admin/posts", H);
    setPosts(po.data);
  };

  const savePost = async () => {
    if (!editing.title) {
      toast.error("Title is required.");
      return;
    }
    try {
      setLoading(true);
      const body = {
        title: editing.title,
        excerpt: editing.excerpt,
        cover_image: editing.cover_image || null,
        content: editing.content,
        published: editing.published,
      };
      if (editing.id) {
        await api.put(`/admin/posts/${editing.id}`, body, H);
        toast.success("Post updated.");
      } else {
        await api.post("/admin/posts", body, H);
        toast.success("Post published.");
      }
      setEditing(null);
      await refreshPosts();
    } catch {
      toast.error("Could not save post.");
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/posts/${id}`, H);
      toast.success("Post deleted.");
      await refreshPosts();
    } catch {
      toast.error("Could not delete.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-sm w-full">
          <p className="font-serif text-4xl font-light">Admin</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your access token to view submissions.
          </p>
          <input
            type="password"
            data-testid="admin-token"
            className="field-underline mt-8"
            placeholder="Admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadAll()}
          />
          <button
            data-testid="admin-login"
            onClick={loadAll}
            disabled={loading}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
          </button>
        </div>
      </div>
    );
  }

  const TABS = [
    ["leads", `Leads (${leads.length})`],
    ["payments", `Payments (${payments.length})`],
    ["posts", `Posts (${posts.length})`],
    ["subscribers", `Subscribers (${subs.length})`],
  ];

  return (
    <div className="min-h-screen bg-background px-6 md:px-10 py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-serif text-5xl font-light">Dashboard</h1>
          <div className="flex gap-2 flex-wrap">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                data-testid={`admin-tab-${id}`}
                onClick={() => setTab(id)}
                className={`rounded-full px-5 py-2.5 text-sm uppercase tracking-[0.14em] border ${
                  tab === id ? "bg-foreground text-background border-foreground" : "border-border"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* LEADS */}
        {tab === "leads" && (
          <Table
            head={["Name", "Email", "Type", "Path", "Message", "Date"]}
            rows={leads.map((l) => [
              l.name,
              l.email,
              l.lead_type,
              l.path || "—",
              l.message || "—",
              new Date(l.created_at).toLocaleDateString(),
            ])}
            empty="No leads yet."
          />
        )}

        {/* PAYMENTS */}
        {tab === "payments" && (
          <Table
            head={["Package", "Amount", "Path", "Status", "Session", "Date"]}
            rows={payments.map((p) => [
              p.package_id,
              `$${p.amount?.toLocaleString()}`,
              p.path || "—",
              p.payment_status,
              p.session_id,
              new Date(p.created_at).toLocaleDateString(),
            ])}
            empty="No payments yet."
          />
        )}

        {/* SUBSCRIBERS */}
        {tab === "subscribers" && (
          <Table
            head={["Email", "Name", "Date"]}
            rows={subs.map((s) => [s.email, s.name || "—", new Date(s.created_at).toLocaleDateString()])}
            empty="No subscribers yet."
          />
        )}

        {/* POSTS */}
        {tab === "posts" && (
          <div className="mt-10">
            <button
              data-testid="admin-new-post"
              onClick={() => setEditing({ ...EMPTY_POST })}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm uppercase tracking-[0.16em] font-medium text-primary-foreground"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New Post
            </button>

            <div className="mt-8 space-y-3">
              {posts.length === 0 && (
                <p className="text-muted-foreground">No posts yet. Write your first one!</p>
              )}
              {posts.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-border p-5"
                >
                  <div>
                    <p className="font-serif text-xl">{p.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      /{p.slug} · {p.published ? "Published" : "Draft"} ·{" "}
                      {new Date(p.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`admin-edit-${p.slug}`}
                      onClick={() => setEditing({ ...p, cover_image: p.cover_image || "" })}
                      className="p-2 rounded-full border border-border hover:border-primary hover:text-primary"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => deletePost(p.id)}
                      className="p-2 rounded-full border border-border hover:border-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Post editor overlay */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 backdrop-blur-sm p-4 md:p-10">
          <div className="w-full max-w-2xl rounded-3xl bg-background border border-border p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-3xl font-light">
                {editing.id ? "Edit post" : "New post"}
              </h2>
              <button onClick={() => setEditing(null)} className="p-2">
                <X strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-6 space-y-6">
              <input
                data-testid="post-title"
                className="field-underline"
                placeholder="Title"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              />
              <input
                data-testid="post-excerpt"
                className="field-underline"
                placeholder="Short excerpt / summary"
                value={editing.excerpt}
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              />
              <input
                data-testid="post-cover"
                className="field-underline"
                placeholder="Cover image URL (optional)"
                value={editing.cover_image}
                onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
              />
              <textarea
                data-testid="post-content"
                rows={12}
                className="field-underline resize-y"
                placeholder="Write your post… Use ## for headings and - for bullets. Separate paragraphs with a blank line."
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              />
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  data-testid="post-published"
                  checked={editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                />
                Published (visible on the site)
              </label>
              <button
                data-testid="post-save"
                onClick={savePost}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Post"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Table({ head, rows, empty }) {
  return (
    <div className="mt-10 overflow-x-auto rounded-2xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left">
          <tr>
            {head.map((h) => (
              <th key={h} className="p-4 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={head.length} className="p-8 text-center text-muted-foreground">
                {empty}
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((c, j) => (
                <td key={j} className="p-4 max-w-xs truncate">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
